import React, { useLayoutEffect, useRef } from "react";
import Select, { components as selectComponents } from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncSelect from "react-select/async";
import { customStyles } from "./selectStyles";

const MENU_MAX_HEIGHT = 280;
const MENU_GAP = 8;

function chainStyle(baseFn, userFn) {
  if (typeof userFn !== "function") return baseFn;
  if (typeof baseFn !== "function") return userFn;
  return (provided, state) => userFn(baseFn(provided, state), state);
}

function mergeSelectStyles(userStyles) {
  const keys = new Set([
    ...Object.keys(customStyles),
    ...(userStyles ? Object.keys(userStyles) : []),
  ]);
  const merged = {};
  keys.forEach((key) => {
    merged[key] = chainStyle(customStyles[key], userStyles?.[key]);
  });

  const menuList = merged.menuList;
  merged.menuList = (provided, state) => {
    const next = typeof menuList === "function" ? menuList(provided, state) : provided;
    return {
      ...next,
      maxHeight: MENU_MAX_HEIGHT,
      overflowY: "auto",
      scrollbarWidth: "thin",
      msOverflowStyle: "auto",
    };
  };

  const stripZ = (styleFn) => (provided, state) => {
    const next = typeof styleFn === "function" ? styleFn(provided, state) : provided;
    const { zIndex, ...rest } = next || {};
    return rest;
  };
  merged.menu = stripZ(merged.menu);
  merged.menuPortal = stripZ(merged.menuPortal);

  return merged;
}

const styleCache = new WeakMap();
const baseStyles = mergeSelectStyles(null);

function stylesFor(userStyles) {
  if (!userStyles) return baseStyles;
  const cached = styleCache.get(userStyles);
  if (cached) return cached;
  const merged = mergeSelectStyles(userStyles);
  styleCache.set(userStyles, merged);
  return merged;
}

function portalTarget() {
  return typeof document !== "undefined" ? document.body : null;
}

function refitOpenMenu(menu) {
  const control = document.querySelector('[class*="__control--menu-is-open"]');
  const list = menu.querySelector('[role="listbox"]');
  const portal = menu.parentElement;
  if (!control || !list || !portal) return;

  const controlRect = control.getBoundingClientRect();
  const spaceBelow = window.innerHeight - controlRect.bottom - MENU_GAP;
  const spaceAbove = controlRect.top - MENU_GAP;
  const openUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
  const side = openUp ? spaceAbove : spaceBelow;
  const height = side >= MENU_MAX_HEIGHT ? MENU_MAX_HEIGHT : Math.max(160, Math.floor(side));

  list.style.maxHeight = `${height}px`;
  portal.style.position = "fixed";
  portal.style.left = `${controlRect.left}px`;
  portal.style.width = `${controlRect.width}px`;
  portal.style.right = "auto";

  if (openUp) {
    portal.style.top = `${controlRect.top}px`;
    menu.style.top = "auto";
    menu.style.bottom = "100%";
  } else {
    portal.style.top = `${controlRect.bottom}px`;
    menu.style.bottom = "auto";
    menu.style.top = "100%";
  }
}

function WljOption(props) {
  return (
    <selectComponents.Option {...props}>
      <span className="wlj-select-option-label">{props.children}</span>
      {props.isSelected ? (
        <span className="wlj-select-option-check" aria-hidden="true" />
      ) : null}
    </selectComponents.Option>
  );
}

function WljMenu(props) {
  const localRef = useRef(null);

  useLayoutEffect(() => {
    const menu = localRef.current;
    if (!menu) return undefined;

    const fit = () => refitOpenMenu(menu);
    fit();
    window.addEventListener("resize", fit);
    document.addEventListener("scroll", fit, true);
    return () => {
      window.removeEventListener("resize", fit);
      document.removeEventListener("scroll", fit, true);
    };
  });

  return (
    <selectComponents.Menu
      {...props}
      innerRef={(node) => {
        localRef.current = node;
        const { innerRef } = props;
        if (typeof innerRef === "function") innerRef(node);
        else if (innerRef) innerRef.current = node;
      }}
    />
  );
}

function WljMenuPortal(props) {
  return <selectComponents.MenuPortal {...props} appendTo={portalTarget()} />;
}

function patchSelect(Component) {
  if (!Component || Component.__wljSelectPatched || typeof Component.render !== "function") {
    return;
  }
  const originalRender = Component.render;
  Component.render = function wljSelectRender(props, ref) {
    const placement =
      props.menuPlacement === "top" || props.menuPlacement === "bottom"
        ? props.menuPlacement
        : "auto";
    return originalRender(
      {
        ...props,
        classNamePrefix: props.classNamePrefix || "wlj-select",
        menuPosition: "fixed",
        menuPlacement: placement,
        menuPortalTarget: portalTarget(),
        menuShouldScrollIntoView: false,
        maxMenuHeight: MENU_MAX_HEIGHT,
        minMenuHeight: 140,
        styles: stylesFor(props.styles),
        components: {
          ...props.components,
          Option: props.components?.Option || WljOption,
          Menu: props.components?.Menu || WljMenu,
          MenuPortal: WljMenuPortal,
        },
      },
      ref
    );
  };
  Component.__wljSelectPatched = true;
}

patchSelect(Select);
patchSelect(CreatableSelect);
patchSelect(AsyncSelect);
